import { io } from 'socket.io-client';

// Events arrive at most once in the happy case, but a flaky connection can
// replay one. Remember just enough ids to drop the repeats.
const SEEN_LIMIT = 50;

// Refresh the token this long before it expires rather than waiting for the
// server to reject it — Socket.IO reuses the original auth object on every
// reconnect, so a token that dies while the tab sleeps would retry forever.
const REFRESH_MARGIN_MS = 60 * 1000;

// A renewal that fails backs off to this rather than retrying against an expiry
// already in the past, which would ask the token endpoint once a second forever.
const RENEW_RETRY_MS = 30 * 1000;

// Each renewal that fails in a row doubles that wait, up to this. A token
// endpoint that keeps answering "no" without saying enabled:false otherwise got
// asked every 30 s for as long as the screen stayed open.
const RENEW_RETRY_MAX_MS = 5 * 60 * 1000;

// After this many REJECTED tokens in a row, stop pretending realtime works and
// let the caller go back to fast polling. Only a token the server refused counts
// — see _handleConnectError.
const MAX_AUTH_FAILURES = 3;

// A renewal is a disconnect and a connect we asked for, and the pair is hidden
// from the caller so one blip does not read as "realtime is down". If the
// reconnect never lands, that concealment has to end by itself: otherwise the
// screen believes it has a live socket, slows its polling to match, and goes
// quiet on both channels at once.
const RENEW_BLIP_MAX_MS = 15 * 1000;

/**
 * Every event that means "this order moved".
 *
 * Both are handled identically — re-read the order — so they are listed rather
 * than switched on. Listening only for order.updated would work today, since it
 * is the only one Laravel sends, and would fail silently the day it sends
 * order.cancelled: the server would report a healthy publish to a room this
 * socket is sitting in, and nothing would render.
 *
 * The payment.* events in the server's whitelist are deliberately absent. No
 * caller emits them, and the screens that would have wanted them — the kiosk
 * payment flows — are not on sockets.
 */
const SIGNAL_EVENTS = ['order.updated', 'order.cancelled'];

/**
 * Is this signal worth an API read?
 *
 * A signal may carry `state`: the new values of the columns that changed, named
 * as the order endpoint reports them. When every one already matches what the
 * screen is showing, the read it would trigger has nothing to find — and the
 * same order routinely moves twice in quick succession from two write paths, so
 * that is worth checking.
 *
 * Lives here rather than in the screens because it is one half of a contract
 * with the server: the other half is the rule that every watched column which
 * changed either appears in `state` or sets `must_read`. Two screens already
 * consult it, and a copy of this that drifts would not fail loudly — it would
 * quietly skip a read on one screen and leave a customer watching an order that
 * never moves.
 *
 * Everything fails towards reading. `must_read` wins outright, an unknown key or
 * a missing order means there is nothing to compare, and values compare as
 * strings so a 1 matches a "1" while a null does not match a 0.
 *
 * @param {object} signal   the `data` of an order signal
 * @param {object} current  the order the screen is currently showing
 */
export function signalNeedsRead(signal, current) {
  if (!signal || signal.must_read) return true;

  const state = signal.state;

  if (!state || typeof state !== 'object' || Array.isArray(state)) return true;

  const keys = Object.keys(state);

  // No state at all is not "nothing changed" — it is a sender that had nothing
  // to say about what changed. @see WebSocketNotificationService
  if (keys.length === 0) return true;

  if (!current) return true;

  return keys.some(
    (key) => !(key in current) || String(current[key] ?? '') !== String(state[key] ?? '')
  );
}

/**
 * One order's realtime channel.
 *
 * The server only ever sends a signal ("this order moved"), never the order
 * itself, so `onSignal` means "re-read the order from the API". `onConnectionChange`
 * exists so the screen can keep a polling fallback and slow it down while the
 * socket is actually up.
 */
export default class OrderRealtime {
  /**
   * @param {object} options
   * @param {Function} options.fetchToken  async () => ({ enabled, token, url, expires_in })
   * @param {Function} options.onSignal    called when the order changed, and on every (re)connect
   * @param {Function} options.onConnectionChange called with true/false
   * @param {Function} [options.onRetired] called once if the server declines to
   *   renew the token (realtime switched off, or the order's watch window is over).
   *   The channel has stopped for good by then; the screen keeps polling.
   */
  constructor({ fetchToken, onSignal, onConnectionChange, onRetired }) {
    this.fetchToken = fetchToken;
    this.onSignal = onSignal || (() => {});
    this.onConnectionChange = onConnectionChange || (() => {});
    this.onRetired = onRetired || (() => {});

    this.socket = null;
    this.seen = [];
    this.expiresAtMs = 0;
    this.authFailures = 0;
    this.stopped = false;
    this.renewTimer = null;
    // Renewals failed in a row; sets the backoff. @see _renewToken
    this.renewFailures = 0;
    // True only across a renewal's own disconnect/connect pair, so that blip is
    // not reported upwards as "realtime is down" and does not make the screen
    // speed its polling up and back down for the sake of a few milliseconds.
    this.renewing = false;
    this.renewBlipTimer = null;
    // The token request currently in flight, if any. @see _refreshToken
    this.refreshInFlight = null;
    this.hasConnectedOnce = false;
    // Closed on purpose by suspend(), until wake() opens it again. @see suspend
    this.suspended = false;
  }

  async start() {
    let issued;

    try {
      issued = await this.fetchToken();
    } catch (err) {
      // Realtime is an optimisation. A failure here is not a page error.
      return false;
    }

    if (this.stopped || !issued || !issued.enabled || !issued.token || !issued.url) {
      return false;
    }

    // Deliberately a duration from now rather than the server's absolute
    // tokenExpiresAt: the phone's clock and the server's disagree by minutes on
    // plenty of real devices, and a renewal aimed at the wrong absolute instant
    // either fires early forever or never fires at all.
    this.expiresAtMs = Date.now() + (issued.expires_in || 0) * 1000;

    this.socket = io(issued.url, {
      transports: ['websocket'],
      auth: { token: issued.token },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      reconnectionAttempts: Infinity,
      timeout: 10000,
    });

    // connection.ready lands on the first connect AND on every reconnect, which
    // makes it the natural resync point: anything the socket missed while it was
    // down is picked up by the re-read this triggers.
    this.socket.on('connection.ready', () => {
      const isReconnect = this.hasConnectedOnce;

      this.hasConnectedOnce = true;
      this.authFailures = 0;
      this._endRenewBlip();
      this._scheduleRenewal();
      this.onConnectionChange(true);

      // The FIRST connection.ready is not a resync.
      //
      // A resync exists to catch what was missed while the socket was down. On a
      // reconnect that is a real gap. On the first connect it is not: the screen
      // loaded its data moments earlier as part of the same startup, and asking
      // it to read again doubles the requests of every single page load.
      //
      // What is left uncovered is the gap between that initial read returning and
      // this handshake completing — a few hundred milliseconds. A change landing
      // inside it waits for the next signal or the fallback timer, which is the
      // same thing that already covers a signal dropped in flight.
      //
      // `resync` marks the re-read as "every reconnected client is doing this
      // right now", which is what lets the caller spread them out. A real
      // order.updated carries no such flag and stays prompt.
      if (isReconnect) {
        this.onSignal({ reason: 'reconnected', resync: true, must_read: true });
      }
    });

    SIGNAL_EVENTS.forEach((name) => {
      this.socket.on(name, (payload) => {
        if (this._isRepeat(payload)) return;
        this.onSignal(payload && payload.data ? payload.data : {});
      });
    });

    this.socket.on('disconnect', () => {
      if (this.renewing) return;
      this.onConnectionChange(false);
    });

    // The server cuts a socket loose once its token passes exp. Reaching this
    // means the renewal below did not get there first — a sleeping tab whose
    // timers were throttled, most likely. Renew now; socket.io is already
    // reconnecting underneath.
    this.socket.on('token.expired', () => {
      this._renewToken();
    });

    this.socket.on('connect_error', (err) => {
      this._endRenewBlip();
      this.onConnectionChange(false);
      this._handleConnectError(err);
    });

    // The server asks everyone to reconnect before it goes down; socket.io's own
    // reconnect handles it, this is only here so the fallback speeds up meanwhile.
    this.socket.on('server.shutdown', () => {
      this.onConnectionChange(false);
    });

    this.socket.io.on('reconnect_attempt', () => {
      if (Date.now() > this.expiresAtMs - REFRESH_MARGIN_MS) {
        this._refreshToken();
      }
    });

    return true;
  }

  /**
   * Bring the socket back after the device stopped running our code.
   *
   * A backgrounded mobile tab is suspended, not slowed: timers stop, the socket
   * is torn down by the OS, and none of socket.io's reconnect logic runs until
   * the tab is foregrounded again. What it then resumes into is a backoff that
   * may be a full delay away, holding a token that expired while the phone was
   * in a pocket. The caller knows when that happened — a visibilitychange, an
   * `online` event — and this is what it calls.
   *
   * Cheap and idempotent by design: calling it on a healthy socket does nothing.
   */
  wake() {
    if (this.stopped || !this.socket) return;

    this.suspended = false;

    // A token at or past its refresh margin will be refused on the next
    // handshake, so swap it before provoking one.
    if (Date.now() > this.expiresAtMs - REFRESH_MARGIN_MS) {
      this._renewToken();
      return;
    }

    // `active` covers a handshake already in flight and socket.io's own
    // reconnect loop. connect() on such a socket is not a no-op: once the
    // transport is open it sends a second CONNECT, and the server ends up
    // holding two sockets for this screen. Only a socket that was closed —
    // suspended, or refused by the auth middleware — needs opening here.
    if (!this.socket.connected && !this.socket.active) {
      this.socket.connect();
    }
  }

  /**
   * Close the socket without ending the channel; wake() opens it again.
   *
   * For a screen nobody is looking at. An open socket costs the server a
   * connection for as long as the tab exists — and a customer who leaves this
   * page in a background tab, or on a desk, keeps it for hours, because the
   * token renews itself. Nothing is lost by closing it: wake() reconnects, and
   * the reconnect's connection.ready makes the screen re-read the order.
   */
  suspend() {
    if (this.stopped || !this.socket || this.suspended) return;

    this.suspended = true;
    clearTimeout(this.renewTimer);
    this.renewTimer = null;
    this._endRenewBlip();

    // A deliberate disconnect: socket.io does not reconnect after one, and the
    // 'disconnect' handler reports it to the screen like any other.
    this.socket.disconnect();
  }

  isConnected() {
    return Boolean(this.socket && this.socket.connected);
  }

  stop() {
    this.stopped = true;

    clearTimeout(this.renewTimer);
    this.renewTimer = null;
    clearTimeout(this.renewBlipTimer);
    this.renewBlipTimer = null;
    this.refreshInFlight = null;

    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.io.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    this.onConnectionChange(false);
  }

  _isRepeat(payload) {
    const id = payload && payload.eventId;
    if (!id) return false;
    if (this.seen.includes(id)) return true;

    this.seen.push(id);
    if (this.seen.length > SEEN_LIMIT) this.seen.shift();

    return false;
  }

  async _handleConnectError(err) {
    const code = err && err.data && err.data.code;

    // TOKEN_EXPIRED is routine; TOKEN_INVALID happens once if the shared secret
    // was rotated. Everything else (CLAIM_INVALID, DEVICE_TYPE_INVALID) is a bug
    // on our side that a new token will not fix.
    if (code !== 'TOKEN_EXPIRED' && code !== 'TOKEN_INVALID') return;

    // Order matters: the failure is only counted once a NEW token has been
    // refused. Counting the attempt first meant a phone that lost signal for
    // half an hour retried with the token it already had — because the refresh
    // could not reach Laravel either — collected three TOKEN_EXPIREDs, and shut
    // realtime down for the rest of the session. The network came back; the
    // socket never did. Three rejected fresh tokens still means what it always
    // meant: something is wrong that reconnecting will not fix.
    const refreshed = await this._refreshToken();

    if (!refreshed) {
      // The auth middleware's refusal destroyed the socket and socket.io will
      // not retry it, so returning here left it dead until a wake() — which the
      // kiosk never calls. Back off and try again; _renewToken reconnects once a
      // token is in hand. A definite no has already retired the channel.
      if (!this.stopped && !this.suspended) {
        this.renewFailures += 1;
        this._scheduleRenewal(this._renewBackoffMs());
      }
      return;
    }

    this.authFailures += 1;

    if (this.authFailures > MAX_AUTH_FAILURES) {
      this.stop();
      return;
    }

    // A refusal from the server's auth middleware is final as far as socket.io
    // is concerned: it destroys the socket and does not reconnect by itself, so
    // the new token sits unused unless we ask for the handshake. The cap above
    // is what bounds this loop.
    // Not while `active`: a wake() may already have a handshake in flight, and
    // a second connect() would put a duplicate socket on the server.
    if (
      !this.stopped &&
      !this.suspended &&
      this.socket &&
      !this.socket.connected &&
      !this.socket.active
    ) {
      this.socket.connect();
    }
  }

  /**
   * Swap in a fresh token, at most one request at a time.
   *
   * Two callers race on every failed reconnect: `reconnect_attempt` asks for a
   * token before the attempt, and the `connect_error` that attempt produces asks
   * again. Against an unreachable server, socket.io retries forever — so without
   * this every open Payment Success screen would put two token requests a second
   * onto Laravel for as long as the outage lasted, which is load we generate
   * ourselves at exactly the moment we can least afford it.
   *
   * Sharing the promise rather than dropping the second call keeps both callers
   * answered truthfully: they wanted to know whether a usable token is in hand,
   * and one request can tell them both.
   */
  async _refreshToken() {
    if (this.stopped || !this.socket) return false;

    if (this.refreshInFlight) return this.refreshInFlight;

    this.refreshInFlight = (async () => {
      try {
        const issued = await this.fetchToken();
        if (this.stopped || !this.socket) return false;

        if (issued && issued.enabled && issued.token) {
          this.expiresAtMs = Date.now() + (issued.expires_in || 0) * 1000;
          this.socket.auth = { token: issued.token };
          this.renewFailures = 0;
          return true;
        }

        // A definite no from the server — realtime switched off, or this order
        // past the window it may be watched in. Not a failure to retry: before
        // this, the renewal asked again every 30 s and socket.io kept
        // reconnecting with the dead token, forever, from every open screen.
        if (issued && issued.enabled === false) {
          this._retire('declined');
        }
      } catch (err) {
        // Leave the old token in place; socket.io keeps retrying on its own.
      }

      return false;
    })();

    try {
      return await this.refreshInFlight;
    } finally {
      this.refreshInFlight = null;
    }
  }

  /**
   * Arm the next token renewal.
   *
   * Without an explicit delay this aims at REFRESH_MARGIN_MS before exp, so the
   * swap happens on a healthy socket instead of during the server closing it.
   */
  _scheduleRenewal(delayMs) {
    clearTimeout(this.renewTimer);

    if (this.stopped) return;

    // No expiry from the server means nothing to renew against. Guessing an
    // interval here would poll the token endpoint forever for no reason.
    if (delayMs === undefined && !this.expiresAtMs) return;

    const delay =
      delayMs === undefined
        ? Math.max(1000, this.expiresAtMs - REFRESH_MARGIN_MS - Date.now())
        : delayMs;

    this.renewTimer = setTimeout(() => this._renewToken(), delay);
  }

  // RENEW_RETRY_MS doubled per failure in a row, capped at RENEW_RETRY_MAX_MS.
  _renewBackoffMs() {
    return Math.min(RENEW_RETRY_MS * 2 ** (this.renewFailures - 1), RENEW_RETRY_MAX_MS);
  }

  async _renewToken() {
    if (this.stopped || !this.socket) return;

    if (!(await this._refreshToken())) {
      // Backing off rather than retrying against a stale expiresAtMs, which
      // would compute a delay in the past and hammer the token endpoint once a
      // second. The server-side expiry is the safety net underneath this.
      this.renewFailures += 1;
      this._scheduleRenewal(this._renewBackoffMs());
      return;
    }

    if (this.stopped || !this.socket) return;

    // socket.io authenticates at the handshake and nowhere else, so a new token
    // only takes effect on a new handshake. Doing that here, deliberately,
    // while the old token is still valid costs a few milliseconds. Waiting for
    // the server to close the socket at exp instead costs a reconnect carrying
    // a dead token and a round trip to find that out.
    this._beginRenewBlip();
    this.socket.disconnect().connect();
  }

  _retire(reason) {
    if (this.stopped) return;

    this.stop();
    this.onRetired(reason);
  }

  _beginRenewBlip() {
    this.renewing = true;
    clearTimeout(this.renewBlipTimer);

    // The deadline is the point. `renewing` suppresses the disconnect that this
    // renewal causes, and connection.ready clears it when the reconnect lands —
    // but a reconnect that never lands left it set forever, and every later drop
    // was swallowed with it. The screen kept reporting a live socket, kept
    // polling at the slow rate, and had no channel left that worked.
    this.renewBlipTimer = setTimeout(() => {
      if (!this.renewing) return;
      this.renewing = false;
      this.onConnectionChange(this.isConnected());
    }, RENEW_BLIP_MAX_MS);
  }

  _endRenewBlip() {
    this.renewing = false;
    clearTimeout(this.renewBlipTimer);
    this.renewBlipTimer = null;
  }
}
